import type { DrawOperation } from './types';
import { sortBySeq } from './operations';

/**
 * Undo/history system over the append-only operation list.
 *
 * The list of *active* operations is the source of truth for the current
 * artwork. Undo moves the most recent active operation onto an `undone` stack
 * (it is NOT destroyed). Committing a new operation clears the undone stack
 * (linear history; P0A has no redo requirement). Reset clears everything.
 *
 * Sequence numbers are monotonically increasing and authoritative for replay
 * order. Gaps are permitted (e.g. after a cancelled stroke) and never affect
 * determinism because replay always sorts by seq.
 */
export class History {
  private active: DrawOperation[] = [];
  private undone: DrawOperation[] = [];
  private nextSeq = 0;

  /** The sequence number the next committed operation should use. */
  peekNextSeq(): number {
    return this.nextSeq;
  }

  /** Append a committed operation. Clears the undone stack. */
  commit(op: DrawOperation): void {
    this.active.push(op);
    this.undone = [];
    this.nextSeq = Math.max(this.nextSeq, op.seq + 1);
  }

  canUndo(): boolean {
    return this.active.length > 0;
  }

  /** Reverse the most recent operation (paint OR erase). Returns it, or null. */
  undo(): DrawOperation | null {
    const op = this.active.pop();
    if (!op) return null;
    this.undone.push(op);
    return op;
  }

  /** Clear the entire artwork. Returns the operations that were removed. */
  reset(): DrawOperation[] {
    const removed = this.active;
    this.active = [];
    this.undone = [];
    return removed;
  }

  /** Active operations in replay order (sorted by seq). Defensive copy. */
  operations(): DrawOperation[] {
    return sortBySeq(this.active);
  }

  count(): number {
    return this.active.length;
  }

  /** Replace state from persisted data. `nextSeq` is derived if not supplied. */
  restore(ops: DrawOperation[], nextSeq?: number): void {
    this.active = sortBySeq(ops);
    this.undone = [];
    const maxSeq = this.active.reduce((m, o) => Math.max(m, o.seq), -1);
    this.nextSeq = nextSeq !== undefined ? Math.max(nextSeq, maxSeq + 1) : maxSeq + 1;
  }
}
